import React, { useState } from "react";
import { CompetitionStage, CompetitionStageWritePayload, TIE_BREAKER_OPTIONS } from "../../../types";
import { moveItem } from "../../../utils/array";
import { AnchoredModal } from "../AnchoredModal";
import { Button } from "../Button";

interface StageEditModalProps {
  stage: CompetitionStage;
  onClose: () => void;
  onSave: (payload: CompetitionStageWritePayload) => Promise<void>;
}

// Modale di modifica di una fase (stage). Stato del form locale: inizializzato
// dalla `stage` al mount (la modale viene montata solo all'apertura).
export const StageEditModal: React.FC<StageEditModalProps> = ({ stage, onClose, onSave }) => {
  const [editName, setEditName] = useState(stage.name);
  const [editQualifiers, setEditQualifiers] = useState<number>(stage.qualifiers_per_group ?? 0);
  const [editWildcards, setEditWildcards] = useState<number>(stage.wildcards ?? 0);
  const [editTieBreakers, setEditTieBreakers] = useState<string>((stage.tie_breakers ?? []).join(","));
  const [editRankingStartPosition, setEditRankingStartPosition] = useState<number>(
    stage.ranking_start_position ?? 0
  );
  const [saving, setSaving] = useState(false);

  const handleTieBreakersMoveUp = (index: number) => {
    setEditTieBreakers(moveItem(editTieBreakers.split(","), index, index - 1).join(","));
  };

  const handleTieBreakersMoveDown = (index: number) => {
    setEditTieBreakers(moveItem(editTieBreakers.split(","), index, index + 1).join(","));
  };

  const handleSave = async () => {
    if (saving) return;
    const payload: CompetitionStageWritePayload = {
      name: editName.trim() || stage.name,
      ranking_start_position: editRankingStartPosition > 0 ? editRankingStartPosition : null,
    };
    if (stage.stage_type === "groups") {
      payload.qualifiers_per_group = editQualifiers;
      payload.wildcards = editWildcards;
      payload.tie_breakers = editTieBreakers
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    }
    setSaving(true);
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
    onClose();
  };

  return (
    <AnchoredModal
      title="Modifica fase"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="text-sm">
          Nome fase
          <input
            type="text"
            className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
        </label>

        {stage.stage_type === "groups" && (
          <>
            <label className="text-sm">
              Qualificati per girone
              <input
                type="number"
                min={0}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                value={editQualifiers}
                onChange={(e) => setEditQualifiers(Number(e.target.value))}
              />
            </label>

            <label className="text-sm">
              Wildcards
              <input
                type="number"
                min={0}
                className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                value={editWildcards}
                onChange={(e) => setEditWildcards(Number(e.target.value))}
              />
            </label>

            <label className="text-sm">
              Criteri di spareggio
              <div className="mt-1 w-full rounded px-2 py-1">
                {editTieBreakers.split(",").filter(Boolean).map((val, index, arr) => (
                  <div
                    key={val}
                    className="flex items-center justify-between rounded mb-1 bg-gray-200 px-3 py-1"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 text-sm text-gray-500">{index + 1}</span>
                      <span className="font-medium">
                        {TIE_BREAKER_OPTIONS.find((option) => option.value === val)?.label}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleTieBreakersMoveUp(index)}
                        disabled={index === 0}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTieBreakersMoveDown(index)}
                        disabled={index === arr.length - 1}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-30"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </label>
          </>
        )}

        <div>
          <label className="text-sm flex justify-between items-center">
            E' una fase finale?
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => setEditRankingStartPosition(1)}
                variant={editRankingStartPosition > 0 ? "primary" : "ghost"}
              >
                Si
              </Button>
              <Button
                type="button"
                onClick={() => setEditRankingStartPosition(0)}
                variant={editRankingStartPosition === 0 ? "primary" : "ghost"}
              >
                No
              </Button>
            </div>
          </label>
          {editRankingStartPosition > 0 ? (
            <>
              <i className="text-xs block my-1 text-gray-600">
                Si: il risultato di questa fase andrà a determinare la classifica finale del torneo
              </i>
              <label className="text-sm">
                Posizione inizio ranking
                <input
                  type="number"
                  min={0}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                  value={editRankingStartPosition}
                  onChange={(e) => setEditRankingStartPosition(Number(e.target.value))}
                />
              </label>
            </>
          ) : (
            <i className="text-xs block my-1 text-gray-600">
              No: questa fase servirà a determinare i partecipanti alla fase successiva.
            </i>
          )}
        </div>
      </div>
    </AnchoredModal>
  );
};
